import { Link } from "react-router-dom";
import { ArrowRight, Zap, Target, Heart, Sparkles } from "lucide-react";

const WS = "https://customer-assets-eiarnc6j.emergentagent.net/job_ddc48b91-037a-4cec-a375-a1288549b26a/artifacts/";
const workshopShots = [
  { src: `${WS}8057a047c2d8d74b_WorkshopCNC.png`, alt: "CNC router in the workshop", label: "THE CNC BEAST", color: "bg-[var(--obvious-yellow)] text-black" },
  { src: `${WS}e880e4006a82573b_Print%20room.png`, alt: "Print room with large-format printer and vinyl cutter", label: "PRINT ROOM", color: "bg-[var(--obvious-teal)] text-black" },
  { src: `${WS}3e224d88a9782a69_Workshop.png`, alt: "Workbench with mitre saws and tool wall", label: "CHOP SHOP", color: "bg-[var(--obvious-coral)] text-white" },
  { src: `${WS}d0c5a593f9d1e5e4_WorkshopSaw.png`, alt: "Panel saw and timber store", label: "SAW HQ", color: "bg-[var(--obvious-purple)] text-white" },
];


const AboutPage = () => {
  const values = [
    {
      icon: Zap,
      title: "BOLD",
      description: "We don't do subtle. Every print, piece and sign the collective makes is designed to grab attention and hold it hostage.",
      color: "bg-[var(--obvious-coral)]",
    },
    {
      icon: Target,
      title: "CUSTOM",
      description: "No off-the-shelf rubbish. If you wanted generic, you'd be at a chain print shop right now. Not here. Not with us.",
      color: "bg-[var(--obvious-teal)]",
    },
    {
      icon: Heart,
      title: "CRAFTED",
      description: "Hand-drawn, hand-cut, hand-sprayed, hand-packed. Made by humans with proper materials. None of that cheap stuff.",
      color: "bg-[var(--obvious-yellow)]",
    },
    {
      icon: Sparkles,
      title: "OBVIOUS",
      description: "Art, prints and signs should be seen. That's literally the point. We get it. Most don't.",
      color: "bg-[var(--obvious-purple)]",
    },
  ];

  return (
    <main className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="min-h-[60vh] bg-[var(--obvious-black)] relative overflow-hidden flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-3xl">
            <span className="font-['Space_Mono'] text-lg bg-[var(--obvious-teal)] text-black px-4 py-2 border-4 border-[var(--obvious-white)] inline-block mb-6">
              WHO WE ARE
            </span>
            <h1 className="font-['Anton'] text-6xl md:text-8xl text-[var(--obvious-white)] leading-[0.9] mb-6">
              THE OBVIOUS
              <br />
              <span className="text-[var(--obvious-coral)]">COLLECTIVE</span>
            </h1>
            <p className="font-['Space_Mono'] text-xl text-gray-300 leading-relaxed">
              A crew of artists, printers, designers &amp; sign-slingers who believe one thing: 
              <strong className="text-[var(--obvious-yellow)]"> art should be seen.</strong> If you want invisible, we're not your people.
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-40 h-40 border-8 border-[var(--obvious-purple)] rounded-full opacity-40 spin-slow" />
        <div className="absolute bottom-20 right-40 w-24 h-24 bg-[var(--obvious-coral)] rotate-45 opacity-30 floating" />
        <div className="absolute top-40 right-60 w-16 h-16 bg-[var(--obvious-yellow)] opacity-40 floating" style={{ animationDelay: '1s' }} />
      </section>

      {/* Story Section */}
      <section className="py-20 bg-[var(--obvious-white)] relative" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="font-['Anton'] text-4xl md:text-6xl text-[var(--obvious-black)]">
                THE STORY
              </h2>
              <div className="space-y-6 font-['Space_Mono'] text-lg text-gray-700">
                <p>
                  We started The Obvious Collective because we were tired of the beige. 
                  Generic prints, forgettable signs, art that nobody looks twice at – 
                  <strong> that's not making. That's just filling walls.</strong>
                </p>
                <p>
                  We're a crew of painters, printers, designers, CNC operators and sign-slingers 
                  under one roof. We print, cut, spray, screen-print, wire and install – and when 
                  the brief calls for it, we'll design from scratch too. Got your own artwork? 
                  Even easier. Whether it's a <strong>limited-run art print</strong> for your front room 
                  or a <strong> 10ft illuminated statement</strong> for your shop front, we'll make it loud.
                </p>
                <p>
                  Our kit includes a CNC router, pro spray booth, print setup and enough LEDs 
                  to light a small town. We work with paper, wood, acrylic, aluminium, brass and foam – 
                  if it can be printed, cut, sprayed or wired, we've probably had a go.
                </p>
                <p>
                  And when it's big enough to need fitting? <strong className="text-[var(--obvious-purple)]">We install, UK-wide.</strong> Scaffolding, 
                  working at height, the full service. We don't just make the thing and wave goodbye – 
                  we see it through to the wall.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="card-90s p-4 -rotate-2 hover:rotate-0 transition-transform" data-testid="about-image">
                <div className="grid grid-cols-2 gap-3">
                  {workshopShots.map((shot, i) => (
                    <figure key={shot.label} className={`relative border-4 border-[var(--obvious-black)] overflow-hidden group ${i % 2 === 0 ? 'rotate-1' : '-rotate-1'}`} data-testid={`about-workshop-${i}`}>
                      <img src={shot.src} alt={shot.alt} loading="lazy" className="w-full h-40 md:h-48 object-cover transition-transform duration-500 group-hover:scale-110" />
                      <figcaption className={`absolute bottom-2 left-2 font-['Anton'] text-xs md:text-sm px-2 py-1 border-2 border-[var(--obvious-black)] ${shot.color}`}>
                        {shot.label}
                      </figcaption>
                    </figure>
                  ))}
                </div>
                <p className="font-['Anton'] text-xl mt-4 text-center">
                  THE COLLECTIVE'S HQ
                </p>
              </div>
              {/* Badge */}
              <div className="absolute -top-6 -left-6 w-28 h-28 bg-[var(--obvious-coral)] border-4 border-[var(--obvious-black)] rounded-full flex items-center justify-center -rotate-12 bounce-custom z-20">
                <span className="font-['Anton'] text-sm text-center text-white leading-tight">
                  HAND<br/>CRAFTED
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[var(--obvious-yellow)] relative" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-black)] mb-4">
              WHAT WE BELIEVE
            </h2>
            <div className="w-32 h-2 bg-[var(--obvious-black)] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={value.title}
                className={`${value.color} p-8 border-4 border-[var(--obvious-black)] shadow-[8px_8px_0px_0px_#000] hover:shadow-[12px_12px_0px_0px_#000] transition-all hover:-translate-y-2 ${index % 2 === 0 ? 'rotate-1' : '-rotate-1'} hover:rotate-0`}
                data-testid={`value-card-${value.title.toLowerCase()}`}
              >
                <div className="w-16 h-16 bg-[var(--obvious-white)] border-4 border-[var(--obvious-black)] flex items-center justify-center mb-6">
                  <value.icon size={32} strokeWidth={3} className="text-black" />
                </div>
                <h3 className={`font-['Anton'] text-2xl mb-4 ${value.title === 'OBVIOUS' ? 'text-white' : 'text-[var(--obvious-black)]'}`}>
                  {value.title}
                </h3>
                <p className={`font-['Space_Mono'] text-sm ${value.title === 'OBVIOUS' ? 'text-gray-200' : 'text-gray-800'}`}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative */}
        <div className="absolute top-10 right-10 w-20 h-20 border-8 border-[var(--obvious-black)] rounded-full opacity-30" />
        <div className="absolute bottom-10 left-10 w-16 h-16 bg-[var(--obvious-coral)] rotate-45 opacity-40" />
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[var(--obvious-black)]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-white)] mb-4">
              HOW WE WORK
            </h2>
            <div className="w-32 h-2 bg-[var(--obvious-teal)] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "BRIEF", desc: "Tell us what you want. Bring artwork, a mood board, or just a half-formed idea — the collective takes it from wherever you're at and runs." },
              { step: "02", title: "DESIGN", desc: "Need design help? We'll sketch, mock up and refine. Got your own? Even better — we go straight to tech specs. Nothing moves until you're 100% in." },
              { step: "03", title: "MAKE", desc: "Printed, cut, sprayed, wired, finished. All in-house. Proper materials, proper hands. No outsourcing, no shortcuts." },
              { step: "04", title: "DELIVER", desc: "Prints posted. Art hand-packed. Signs installed UK-wide — scaffolding, working at height, the lot. We don't leave until it's perfect." },
            ].map((item) => (
              <div
                key={item.step}
                className="relative p-8 border-4 border-[var(--obvious-white)] bg-transparent hover:bg-[var(--obvious-purple)] transition-colors group"
                data-testid={`process-step-${item.step}`}
              >
                <span className="font-['Anton'] text-8xl text-[var(--obvious-teal)] opacity-30 absolute top-2 right-4 group-hover:text-[var(--obvious-yellow)] transition-colors">
                  {item.step}
                </span>
                <h3 className="font-['Anton'] text-3xl text-[var(--obvious-white)] mb-4 relative z-10">
                  {item.title}
                </h3>
                <p className="font-['Space_Mono'] text-gray-400 group-hover:text-gray-200 transition-colors relative z-10">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Brand Graphic */}
      <section className="py-20 bg-[var(--obvious-coral)] relative overflow-hidden" data-testid="about-cta">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div className="text-center md:text-left">
              <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-white)] mb-6">
                READY TO WORK
                <br />
                WITH THE CREW?
              </h2>
              <p className="font-['Space_Mono'] text-lg text-white/80 mb-10 max-w-2xl">
                No piece is too big, too weird or too loud. Prints, one-offs, signs, commissions — if you want it seen, 
                we want to make it. If you want beige and boring, we'll happily recommend someone else.
              </p>
              <Link
                to="/contact"
                className="btn-obvious bg-[var(--obvious-black)] text-white inline-flex items-center gap-2 text-2xl"
                data-testid="about-cta-button"
              >
                LET'S TALK <ArrowRight size={28} strokeWidth={3} />
              </Link>
            </div>
            
            {/* Brand Graphic */}
            <div className="flex justify-center">
              <div className="card-90s p-4 bg-[var(--obvious-black)] rotate-3 hover:rotate-0 transition-transform">
                <img 
                  src="https://customer-assets.emergentagent.com/job_funky-nineties/artifacts/ln7q3ug6_ChatGPT%20Image%20Feb%205%2C%202026%2C%2010_46_20%20PM.png"
                  alt="Let's Make Something Obvious"
                  className="w-full max-w-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Decorative */}
        <div className="absolute top-10 left-10 w-32 h-32 border-8 border-[var(--obvious-white)] rounded-full opacity-30 spin-slow" />
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-[var(--obvious-yellow)] opacity-30 rotate-45 floating" />
      </section>
    </main>
  );
};

export default AboutPage;
